"use client";

import {
	ImageIcon,
	Loader2,
	Mic,
	Music2,
	Plus,
	RotateCcw,
	Send,
	Settings2,
	X,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
	Conversation,
	ConversationContent,
	ConversationEmptyState,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import {
	MusicSidebar,
	type MusicHistoryItem,
} from "@/components/playground/music-sidebar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/hooks/useUser";

import { PRICING_CONFIG } from "@routerbench/shared";

import type { Organization, Project } from "@/lib/types";
import type {
	ModelDefinition,
	ProviderDefinition,
	ProviderModelMapping,
} from "@routerbench/models";

type ResponseFormat = "mp3" | "wav";

interface RefImage {
	dataUrl: string;
	name: string;
}

interface Turn {
	localId: string;
	status: "in_progress" | "completed" | "failed";
	prompt: string;
	refUrls: string[];
	model: string;
	responseFormat: ResponseFormat;
	/** Cached blob URL for the result so <audio> can stream & seek smoothly. */
	audioUrl?: string;
	mimeType?: string;
	lyrics?: string | null;
	costInr?: number;
	error?: string;
	/** Snapshot of the request body so the turn can be regenerated exactly. */
	payload?: Record<string, unknown>;
}

interface MusicPageClientProps {
	models: ModelDefinition[];
	providers: ProviderDefinition[];
	organizations: Organization[];
	selectedOrganization: Organization | null;
	projects: Project[];
	selectedProject: Project | null;
}

function readFileAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () =>
			typeof reader.result === "string"
				? resolve(reader.result)
				: reject(new Error("read failed"));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
}

/**
 * Gateway returns proxy paths like "/v1/music/{id}/content" so the bucket
 * can stay private. The playground proxies the gateway under "/api/music".
 * Remap one to the other for browser fetches.
 */
function toPlaygroundUrl(url: string): string {
	if (url.startsWith("/v1/music/")) {
		return url.replace(/^\/v1\/music\//, "/api/music/");
	}
	if (url.startsWith("/v1/videos/")) {
		return url.replace(/^\/v1\/videos\//, "/api/videos/");
	}
	return url;
}

function base64ToBlobUrl(b64: string, mime: string): string {
	const bin = atob(b64);
	const len = bin.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i++) {
		bytes[i] = bin.charCodeAt(i);
	}
	return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

export default function MusicPageClient({
	models,
	organizations,
	selectedOrganization: initialOrg,
	selectedProject: initialProject,
}: MusicPageClientProps) {
	const { user } = useUser();
	const router = useRouter();
	const isAuthenticated = !!user;

	const selectedOrganization = initialOrg;
	const selectedProject = initialProject;

	const musicModels = useMemo(
		() => models.filter((m) => m.output?.includes("audio_music")),
		[models],
	);
	// Default to Clip — cheapest and shortest, best for getting started.
	const [selectedModel, setSelectedModel] = useState<string>(
		musicModels.find((m) => m.id === "lyria-3-clip-preview")?.id ||
			musicModels[0]?.id ||
			"",
	);

	const mapping = useMemo<ProviderModelMapping | undefined>(() => {
		const baseId = selectedModel.includes("/")
			? selectedModel.split("/", 2)[1]
			: selectedModel;
		const m = musicModels.find((mm) => mm.id === baseId);
		return m?.providers.find(
			(p) => (p as ProviderModelMapping).audioMusicGenerations === true,
		) as ProviderModelMapping | undefined;
	}, [selectedModel, musicModels]);

	const allowedFormats: ResponseFormat[] = useMemo(
		() => (mapping?.audioMusicOutputFormats as ResponseFormat[]) ?? ["mp3"],
		[mapping],
	);
	const maxReferenceImages = mapping?.audioMusicMaxReferenceImages ?? 0;
	const supportsReference = maxReferenceImages > 0;
	const typicalDuration = mapping?.audioMusicTypicalDurationSeconds ?? 30;

	// Composer state
	const [prompt, setPrompt] = useState("");
	const [responseFormat, setResponseFormat] = useState<ResponseFormat>("mp3");
	const [instrumental, setInstrumental] = useState(false);
	const [lyrics, setLyrics] = useState("");
	const [references, setReferences] = useState<RefImage[]>([]);

	const [turns, setTurns] = useState<Turn[]>([]);

	// Track every blob: URL we create from base64 so we can revoke them all on
	// unmount / "New song" without depending on a (possibly stale) turns
	// snapshot. A plain Set in a ref is stable across renders.
	const blobUrlsRef = useRef<Set<string>>(new Set());
	const revokeAllBlobUrls = useCallback(() => {
		blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
		blobUrlsRef.current.clear();
	}, []);

	// Persistent history (from /v1/music via the gateway). Refreshes after
	// each generation so the freshly-saved S3 track appears at the top.
	const [history, setHistory] = useState<MusicHistoryItem[]>([]);
	const [historyLoading, setHistoryLoading] = useState(false);

	// Drop unsupported state when switching models.
	useEffect(() => {
		if (!allowedFormats.includes(responseFormat)) {
			setResponseFormat(allowedFormats[0]);
		}
	}, [allowedFormats, responseFormat]);
	useEffect(() => {
		if (!supportsReference && references.length > 0) {
			setReferences([]);
		}
	}, [supportsReference, references.length]);

	// Free every blob URL we created when the page unmounts (the ref always
	// holds the current set, so this isn't a stale-closure no-op).
	useEffect(() => {
		return () => revokeAllBlobUrls();
	}, [revokeAllBlobUrls]);

	// Ensure a playground API key for the selected project.
	const ensuredProjectRef = useRef<string | null>(null);
	const [keyReady, setKeyReady] = useState(false);
	useEffect(() => {
		if (!isAuthenticated || !selectedProject) {
			ensuredProjectRef.current = null;
			setKeyReady(false);
			return;
		}
		if (ensuredProjectRef.current === selectedProject.id) {
			setKeyReady(true);
			return;
		}
		setKeyReady(false);
		fetch("/api/ensure-playground-key", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ projectId: selectedProject.id }),
		})
			.then(() => {
				ensuredProjectRef.current = selectedProject.id;
				setKeyReady(true);
			})
			.catch(() => {
				/* Generate stays disabled until a key is ready */
			});
	}, [isAuthenticated, selectedProject]);

	const busy = turns.some((t) => t.status === "in_progress");

	// Load history once the API key is ready, and refresh after each
	// completed generation so the freshly-stored S3 track shows up at the top.
	const loadHistory = useCallback(async () => {
		setHistoryLoading(true);
		try {
			const r = await fetch("/api/music/history?limit=30", {
				cache: "no-store",
			});
			if (!r.ok) {
				return;
			}
			const data = await r.json();
			setHistory(Array.isArray(data?.data) ? data.data : []);
		} catch {
			/* keep prior list */
		} finally {
			setHistoryLoading(false);
		}
	}, []);
	useEffect(() => {
		if (keyReady) {
			void loadHistory();
		}
	}, [keyReady, loadHistory]);

	const onPickHistory = useCallback(
		(item: MusicHistoryItem) => {
			if (!item.audio?.url) {
				return;
			}
			// Each history item gets a STABLE id (`hist-<item.id>`), so clicking
			// two different tracks adds two distinct turns, and re-clicking the
			// same track doesn't stack duplicates. (Previously the id used
			// Date.now(), so every click appended an ever-growing duplicate.)
			const histLocalId = `hist-${item.id}`;
			setTurns((prev) => {
				if (prev.some((t) => t.localId === histLocalId)) {
					// Already open — don't duplicate it.
					return prev;
				}
				return [
					...prev,
					{
						localId: histLocalId,
						status: "completed",
						prompt: item.prompt || "(no prompt)",
						refUrls: [],
						model: item.metadata?.used_model || "lyria-3-clip-preview",
						responseFormat:
							(item.metadata?.response_format as ResponseFormat) || "mp3",
						audioUrl: toPlaygroundUrl(item.audio!.url),
						mimeType: item.audio!.mime_type,
						lyrics: null,
						costInr:
							typeof item.usage?.cost_inr === "number"
								? item.usage.cost_inr
								: undefined,
					},
				];
			});
			// Reflect the selected track in the URL so each history item is its
			// own addressable route (preserve any existing query params).
			const params = new URLSearchParams(window.location.search);
			params.set("track", item.id);
			router.replace(`/music?${params.toString()}`, { scroll: false });
			// Always bring the picked track into view — whether it was just
			// appended or was already in the conversation (deduped) — so a click
			// reliably *shows* it instead of silently no-op'ing.
			requestAnimationFrame(() => {
				const el = document.getElementById(`turn-${histLocalId}`);
				el?.scrollIntoView({ behavior: "smooth", block: "center" });
			});
		},
		[router],
	);

	// Deep-link: if the URL has ?track=<id>, open that track once history has
	// loaded (one-shot). Makes the per-track route reload/share-able.
	const trackRestoredRef = useRef(false);
	useEffect(() => {
		if (trackRestoredRef.current || history.length === 0) {
			return;
		}
		const trackId = new URLSearchParams(window.location.search).get("track");
		if (!trackId) {
			return;
		}
		const item = history.find((h) => h.id === trackId);
		if (item?.audio?.url) {
			trackRestoredRef.current = true;
			onPickHistory(item);
		}
	}, [history, onPickHistory]);

	// Clear the ?track param (used when starting fresh / new song).
	const clearTrackParam = useCallback(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("track")) {
			params.delete("track");
			const qs = params.toString();
			router.replace(qs ? `/music?${qs}` : "/music", { scroll: false });
		}
	}, [router]);

	const estInr = useMemo(
		() =>
			(mapping?.audioMusicPricePerGeneration ?? 0) *
			PRICING_CONFIG.usdToInrRate,
		[mapping],
	);

	const canGenerate =
		keyReady &&
		!busy &&
		!!selectedProject &&
		!!selectedModel &&
		!!prompt.trim();

	// ── Reference image handlers ────────────────────────────────────────
	const onAddReferences = useCallback(
		async (files: FileList | null) => {
			if (!files) {
				return;
			}
			const room = maxReferenceImages - references.length;
			const picked = Array.from(files).slice(0, Math.max(0, room));
			const next: RefImage[] = [];
			for (const file of picked) {
				if (file.size > 8 * 1024 * 1024) {
					toast.error(`${file.name} too large (max 8MB)`);
					continue;
				}
				next.push({ dataUrl: await readFileAsDataUrl(file), name: file.name });
			}
			if (next.length) {
				setReferences((prev) =>
					[...prev, ...next].slice(0, maxReferenceImages),
				);
			}
		},
		[maxReferenceImages, references.length],
	);

	// ── Submit ──────────────────────────────────────────────────────────
	const submitJob = useCallback(
		async (payload: Record<string, unknown>, turn: Turn) => {
			setTurns((prev) => [...prev, { ...turn, payload }]);
			try {
				const r = await fetch("/api/music", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				});
				const data = await r.json();
				if (!r.ok) {
					// The gateway returns { error: true, status, message } (error is a
					// boolean), while the playground proxy returns { error: "<string>" }.
					// Pick the human-readable string from whichever is present.
					const msg =
						(typeof data?.error === "string" && data.error) ||
						data?.message ||
						"Failed to generate music";
					toast.error(msg);
					setTurns((prev) =>
						prev.map((t) =>
							t.localId === turn.localId
								? { ...t, status: "failed", error: msg }
								: t,
						),
					);
					return;
				}
				// The POST response always includes base64 for instant playback.
				// Prefer it over the S3 proxy `url`: the billing log is inserted
				// asynchronously, so the `/api/music/{id}/content` proxy can 404
				// for a few seconds right after generation. base64 → blob URL has
				// no such race. (History replay uses the durable url instead.)
				const directUrl = data?.audio?.url as string | undefined;
				const b64 = data?.audio?.base64 as string | undefined;
				const mime = (data?.audio?.mime_type as string) || "audio/mpeg";
				if (!directUrl && !b64) {
					setTurns((prev) =>
						prev.map((t) =>
							t.localId === turn.localId
								? { ...t, status: "failed", error: "No audio in response" }
								: t,
						),
					);
					return;
				}
				const url = b64
					? base64ToBlobUrl(b64, mime)
					: toPlaygroundUrl(directUrl as string);
				if (b64) {
					blobUrlsRef.current.add(url);
				}
				console.log("[music] track ready", {
					localId: turn.localId,
					source: b64 ? "base64-blob" : "s3-proxy",
					proxyUrl: directUrl ? toPlaygroundUrl(directUrl) : null,
					audioUrl: url,
					mime,
					base64Length: b64?.length ?? 0,
				});
				setTurns((prev) =>
					prev.map((t) =>
						t.localId === turn.localId
							? {
									...t,
									status: "completed",
									audioUrl: url,
									mimeType: mime,
									lyrics: data.text || null,
									costInr: data?.usage?.cost_inr ?? undefined,
								}
							: t,
					),
				);
				// Refresh history so the new track appears in the sidebar. The
				// billing log row is inserted asynchronously by the worker
				// (Redis queue → DB), so it isn't queryable for a few seconds —
				// refresh now AND after short delays so the new track shows up.
				void loadHistory();
				setTimeout(() => void loadHistory(), 4000);
				setTimeout(() => void loadHistory(), 9000);
			} catch (e) {
				const msg = e instanceof Error ? e.message : "Request failed";
				toast.error(msg);
				setTurns((prev) =>
					prev.map((t) =>
						t.localId === turn.localId
							? { ...t, status: "failed", error: msg }
							: t,
					),
				);
			}
		},
		[loadHistory],
	);

	const handleGenerate = useCallback(() => {
		if (!canGenerate) {
			return;
		}
		// A fresh generation isn't a history pick — drop any stale ?track.
		clearTrackParam();
		const text = prompt.trim();
		const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

		const payload: Record<string, unknown> = {
			model: selectedModel,
			prompt: text,
			response_format: responseFormat,
			...(instrumental ? { instrumental: true } : {}),
			// Custom lyrics only make sense when NOT instrumental.
			...(!instrumental && lyrics.trim() ? { lyrics: lyrics.trim() } : {}),
			...(references.length > 0
				? { reference_images: references.map((r) => r.dataUrl) }
				: {}),
		};

		void submitJob(payload, {
			localId,
			status: "in_progress",
			prompt: text,
			refUrls: references.map((r) => r.dataUrl),
			model: selectedModel,
			responseFormat,
		});

		setPrompt("");
		setReferences([]);
	}, [
		canGenerate,
		prompt,
		responseFormat,
		instrumental,
		lyrics,
		references,
		selectedModel,
		submitJob,
		clearTrackParam,
	]);

	const handleRegenerate = useCallback(
		(source: Turn) => {
			if (busy || !source.payload) {
				return;
			}
			void submitJob(source.payload, {
				localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
				status: "in_progress",
				prompt: source.prompt,
				refUrls: source.refUrls,
				model: source.model,
				responseFormat: source.responseFormat,
			});
		},
		[busy, submitJob],
	);

	const handleNewSong = useCallback(() => {
		// Free any prior blob URLs before clearing the conversation.
		revokeAllBlobUrls();
		setTurns([]);
		setPrompt("");
		setReferences([]);
		setLyrics("");
		clearTrackParam();
	}, [revokeAllBlobUrls, clearTrackParam]);

	const thumb = (src: string, key: string) => (
		<img
			key={key}
			src={src}
			alt=""
			className="h-12 w-12 rounded-md object-cover"
		/>
	);

	return (
		<SidebarProvider>
			<div className="flex h-svh w-full overflow-hidden bg-background">
				<MusicSidebar
					selectedOrganization={selectedOrganization}
					history={history}
					historyLoading={historyLoading}
					onPickHistory={onPickHistory}
				/>
				<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
					{/* Header */}
					<div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
						<SidebarTrigger className="shrink-0" />
						<Music2 className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
						<span className="hidden text-sm font-medium sm:inline">Music</span>
						<div className="mx-1 hidden h-5 w-px shrink-0 bg-border/60 sm:block" />
						<div className="min-w-0 flex-1 sm:max-w-[260px]">
							<Select
								value={selectedModel}
								onValueChange={setSelectedModel}
								disabled={busy}
							>
								<SelectTrigger className="h-9 w-full">
									<SelectValue placeholder="Select a music model" />
								</SelectTrigger>
								<SelectContent>
									{musicModels.map((m) => (
										<SelectItem key={m.id} value={m.id}>
											{m.name || m.id}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="ml-auto hidden md:block">
							<Select
								value={selectedOrganization?.id ?? ""}
								onValueChange={(id) => router.push(`/music?orgId=${id}`)}
								disabled={busy}
							>
								<SelectTrigger className="h-8 w-[170px]">
									<SelectValue placeholder="Organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations.map((o) => (
										<SelectItem key={o.id} value={o.id}>
											{o.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<Button
							variant="outline"
							size="sm"
							className="ml-auto h-8 shrink-0 gap-1.5 md:ml-2"
							onClick={handleNewSong}
							disabled={turns.length === 0 && !prompt.trim()}
							title="Clear and start a new song"
						>
							<Plus className="h-4 w-4" />
							<span className="hidden sm:inline">New song</span>
						</Button>
						<div className="shrink-0">
							<ThemeToggle />
						</div>
					</div>

					{/* Conversation */}
					<Conversation>
						<ConversationContent className="mx-auto w-full max-w-3xl">
							{turns.length === 0 ? (
								<ConversationEmptyState
									icon={<Music2 className="h-10 w-10" />}
									title="Generate music"
									description="Describe the song — genre, mood, instruments, tempo, structure — and press Enter."
								/>
							) : (
								turns.map((turn) => (
									<div key={turn.localId} id={`turn-${turn.localId}`}>
										{/* User turn */}
										<Message from="user">
											<MessageContent variant="flat">
												<div className="whitespace-pre-wrap">{turn.prompt}</div>
												{turn.refUrls.length > 0 && (
													<div className="flex flex-wrap gap-2 pt-1">
														{turn.refUrls.map((u, i) => thumb(u, `r${i}`))}
													</div>
												)}
											</MessageContent>
										</Message>

										{/* Assistant turn */}
										<Message from="assistant">
											<MessageContent
												variant="flat"
												className="w-full max-w-md"
											>
												{turn.status === "completed" && turn.audioUrl ? (
													<div className="flex w-full flex-col gap-2">
														<div className="overflow-hidden rounded-xl border border-border/60 bg-muted/30 p-2">
															<audio
																controls
																src={turn.audioUrl}
																className="block h-11 w-full"
																preload="metadata"
																onError={(e) => {
																	console.error("[music] audio error", {
																		src: turn.audioUrl,
																		code: e.currentTarget.error?.code,
																		message: e.currentTarget.error?.message,
																	});
																}}
															/>
														</div>
														{turn.lyrics && (
															<details className="rounded-md border border-border/60 bg-muted/20 px-3 py-1.5 text-xs">
																<summary className="cursor-pointer select-none font-medium text-muted-foreground">
																	Lyrics &amp; structure
																</summary>
																<pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] leading-snug text-foreground">
																	{turn.lyrics}
																</pre>
															</details>
														)}
														<div className="flex flex-wrap items-center justify-between gap-2">
															<a
																href={turn.audioUrl}
																download={`${turn.localId}.${turn.responseFormat}`}
																className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
															>
																Download {turn.responseFormat.toUpperCase()}
															</a>
															<div className="flex items-center gap-1.5">
																{typeof turn.costInr === "number" && (
																	<span className="text-[11px] text-muted-foreground">
																		${turn.costInr.toFixed(2)}
																	</span>
																)}
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-8"
																	disabled={busy || !turn.payload}
																	onClick={() => handleRegenerate(turn)}
																	title="Regenerate with the same settings"
																>
																	<RotateCcw className="mr-1 h-3.5 w-3.5" />
																	Regenerate
																</Button>
															</div>
														</div>
													</div>
												) : turn.status === "failed" ? (
													<div className="flex w-full flex-col gap-2">
														<div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
															{turn.error ? turn.error : "Generation failed."}
														</div>
														{turn.payload && (
															<div>
																<Button
																	variant="outline"
																	size="sm"
																	className="h-8"
																	disabled={busy}
																	onClick={() => handleRegenerate(turn)}
																>
																	<RotateCcw className="mr-1 h-3.5 w-3.5" />
																	Try again
																</Button>
															</div>
														)}
													</div>
												) : (
													<div className="flex w-full flex-col gap-2">
														<div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3">
															<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
															<div className="flex flex-col">
																<span className="text-sm font-semibold text-foreground">
																	Composing…
																</span>
																<span className="text-[11px] text-muted-foreground">
																	~{Math.round(typicalDuration / 30) * 15 || 15}
																	s typical
																</span>
															</div>
														</div>
													</div>
												)}
											</MessageContent>
										</Message>
									</div>
								))
							)}
						</ConversationContent>
					</Conversation>

					{/* Composer */}
					<div className="shrink-0 border-t bg-background">
						<div className="mx-auto w-full max-w-3xl px-3 py-3">
							{/* Reference image strip (Pro only) */}
							{supportsReference && references.length > 0 && (
								<div className="mb-2 flex flex-wrap items-center gap-2">
									{references.map((r, i) => (
										<div
											key={`${r.name}-${i}`}
											className="relative h-14 w-14 overflow-hidden rounded-md border border-border/60"
										>
											<img
												src={r.dataUrl}
												alt={r.name}
												className="h-full w-full object-cover"
											/>
											<button
												type="button"
												onClick={() =>
													setReferences((prev) =>
														prev.filter((_, j) => j !== i),
													)
												}
												className="absolute right-0.5 top-0.5 rounded-full bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
												title="Remove"
												aria-label="Remove reference image"
											>
												<X className="h-3 w-3" />
											</button>
										</div>
									))}
								</div>
							)}

							<div className="flex items-end gap-2">
								<div className="flex flex-1 flex-col gap-2 rounded-2xl border bg-muted/30 px-3 py-2">
									<Textarea
										value={prompt}
										onChange={(e) => setPrompt(e.target.value)}
										placeholder="Describe the song… (genre, mood, instruments, tempo, structure)"
										className="min-h-[44px] resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
										onKeyDown={(e) => {
											if (e.key === "Enter" && !e.shiftKey) {
												e.preventDefault();
												handleGenerate();
											}
										}}
									/>
									<div className="flex flex-wrap items-center gap-2">
										{supportsReference && (
											<label
												className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
												title={`Attach up to ${maxReferenceImages} reference images`}
											>
												<ImageIcon className="h-3.5 w-3.5" />
												<span>
													Reference
													{references.length > 0
														? ` (${references.length}/${maxReferenceImages})`
														: ""}
												</span>
												<input
													type="file"
													accept="image/*"
													multiple
													className="hidden"
													onChange={(e) => {
														void onAddReferences(e.target.files);
														e.target.value = "";
													}}
												/>
											</label>
										)}

										{/* Custom lyrics — disabled while Instrumental is on. */}
										<Popover>
											<PopoverTrigger asChild>
												<button
													type="button"
													disabled={instrumental}
													className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
													title={
														instrumental
															? "Turn off Instrumental to add lyrics"
															: "Add your own lyrics"
													}
												>
													<Mic className="h-3.5 w-3.5" />
													Lyrics{lyrics.trim() ? " ✓" : ""}
												</button>
											</PopoverTrigger>
											<PopoverContent className="w-80 space-y-2">
												<Label className="text-xs">Custom lyrics</Label>
												<Textarea
													value={lyrics}
													onChange={(e) => setLyrics(e.target.value)}
													placeholder={
														"[Verse 1]\nWalking through the neon glow…\n\n[Chorus]\nWe are the echoes in the night…"
													}
													className="min-h-[140px] resize-y text-xs font-mono"
												/>
												<p className="text-[10px] text-muted-foreground">
													Use section tags like [Verse], [Chorus], [Bridge].
													Sent to Lyria as a{" "}
													<span className="font-mono">Lyrics:</span> block.
													Leave empty to let the model write lyrics.
												</p>
												{lyrics.trim() && (
													<button
														type="button"
														onClick={() => setLyrics("")}
														className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
													>
														Clear lyrics
													</button>
												)}
											</PopoverContent>
										</Popover>

										<Popover>
											<PopoverTrigger asChild>
												<button
													type="button"
													className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
													title="Settings"
												>
													<Settings2 className="h-3.5 w-3.5" />
													Settings
												</button>
											</PopoverTrigger>
											<PopoverContent className="w-72 space-y-3">
												<div className="space-y-1">
													<Label className="text-xs">Output format</Label>
													<Select
														value={responseFormat}
														onValueChange={(v) =>
															setResponseFormat(v as ResponseFormat)
														}
													>
														<SelectTrigger className="h-8 w-full">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															{allowedFormats.map((f) => (
																<SelectItem key={f} value={f}>
																	{f.toUpperCase()}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													{allowedFormats.length === 1 && (
														<p className="text-[10px] text-muted-foreground">
															This model only supports{" "}
															{allowedFormats[0].toUpperCase()}.
														</p>
													)}
												</div>
												<div className="flex items-center justify-between">
													<Label
														htmlFor="instrumental"
														className="text-xs font-normal"
													>
														Instrumental only
													</Label>
													<Switch
														id="instrumental"
														checked={instrumental}
														onCheckedChange={setInstrumental}
													/>
												</div>
											</PopoverContent>
										</Popover>

										<div className="ml-auto text-[11px] text-muted-foreground">
											Est ${estInr.toFixed(2)} / song
										</div>
									</div>
								</div>

								<Button
									size="icon"
									className="h-11 w-11 rounded-2xl"
									disabled={!canGenerate}
									onClick={handleGenerate}
									title="Generate"
								>
									{busy ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Send className="h-4 w-4" />
									)}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</SidebarProvider>
	);
}
